import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Bell, LogOut, User, Menu } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { api, clearAuthToken } from '../utils/api';
import { toast } from 'sonner';
import ThemeToggle from './ThemeToggle';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/e8a02fd5-3c12-4859-8d0e-46bed620e093/images/1193b7a041639f784b7a40c1e423439a99456520d92788df70354c3419c35964.png";

const DashboardHeader = ({ user, unreadCount = 0 }) => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Even if backend logout fails, clear local token below
    } finally {
      clearAuthToken();
      toast.success('Logged out successfully');
      navigate('/login', { replace: true });
    }
  };

  const getRoleDisplay = (role, engineerType) => {
    if (role === 'engineer' && engineerType) {
      return engineerType === 'design' ? 'Design Engineer' : 'Construction Engineer';
    }
    const roles = {
      customer: 'Homeowner',
      engineer: 'Engineer',
      admin: 'Admin'
    };
    return roles[role] || role;
  };

  return (
    <header className="glass border-b border-border sticky top-0 z-40 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo - Hidden on mobile, show hamburger instead */}
          <div className="flex items-center">
            <Link to={`/${user.role}`} className="flex items-center space-x-3">
              <img 
                src={LOGO_URL} 
                alt="MyHomeEngineer" 
                className="w-9 h-9 object-contain"
              />
              <span className="hidden sm:block text-xl font-heading font-bold text-foreground">
                MyHomeEngineer
              </span>
            </Link>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Theme Toggle */}
            <ThemeToggle />
            
            {/* Notifications */}
            <Button
              variant="ghost"
              size="icon"
              className="relative touch-target"
              data-testid="notifications-button"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-secondary text-secondary-foreground text-xs rounded-full">
                  {unreadCount}
                </Badge>
              )}
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center space-x-2 touch-target" data-testid="user-menu-button">
                  <Avatar className="h-9 w-9 ring-2 ring-border">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                      {user.name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-foreground">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{getRoleDisplay(user.role, user.engineer_type)}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 glass-card">
                <div className="px-3 py-2 md:hidden">
                  <p className="font-medium text-foreground">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{getRoleDisplay(user.role, user.engineer_type)}</p>
                </div>
                <DropdownMenuSeparator className="md:hidden" />
                <DropdownMenuItem className="cursor-pointer touch-target">
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout} 
                  className="cursor-pointer text-destructive touch-target" 
                  data-testid="logout-button"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;